import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";
import classnames from "classnames";

import type { ICartAddModifierChoice } from "@/api/interfaces/cartLayouts";
import {
  type IModifier,
  ProductModifierConstraints,
  ProductModifierDisplayMode,
} from "@/api/interfaces/productLayouts";
import Box from "@/base/components/Box/Box";
import { useAppSelector } from "@/store";
import { selectProducts } from "@/store/products/selectors";

import { ModifierChoice } from "./ModifierChoice";

const useStyles = createUseStyles(({ spacing, font, color }) => ({
  extrasRow: {
    background: "transparent",
    padding: 0,
    display: "flex",
    alignItems: "center",
    width: "100%",
    cursor: "pointer",
  },
  row: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "start",
    fontWeight: font.weight.m,
    fontSize: font.size.s,
    lineHeight: font.lineHeight.s,
    color: color.productPageDescriptionText,
    padding: [spacing.l, 0],
  },
  headerBlock: {
    display: "flex",
    flexDirection: "column",
  },
  price: {
    minWidth: 60,
    marginLeft: "auto",
  },
  header: {
    fontSize: 32,
    fontWeight: font.weight.s,
    lineHeight: "34px",
    color: color.productPageHeadingText,
  },
  icon: {
    display: "flex",
    alignItems: "center",
    marginRight: spacing.s,
  },
  itemInfoContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  imgContainer: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.l,
    alignItems: "center",
    padding: [spacing.s, spacing.l],
    position: "relative",
  },
  img: {
    width: 100,
    height: 100,
    borderRadius: "50%",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
  },
  imgTitle: {
    fontSize: font.size.s,
    fontWeight: font.weight.m,
    lineHeight: font.lineHeight.xxs,
    color: color.productPageImageTitle,
    textAlign: "center",
  },
  imgBlock: {
    display: "flex",
    flexWrap: "wrap",
    gap: spacing.xxl,
    justifyContent: "center",
  },
  mandatoryOption: {
    fontWeight: font.weight.m,
    fontSize: font.size.s,
    lineHeight: font.lineHeight.xxs,
    color: color.productPageMandatoryOption,
    paddingTop: spacing.l,
  },
  error: {
    color: color.error,
  },
  checkedIcon: {
    position: "absolute",
    top: -5,
    right: 10,
  },
  extrasBox: {
    gap: 24,
  },
}));

type ExtrasBlockProps = {
  modifier: IModifier;
  cartModifiers: ICartAddModifierChoice[];
  setCartModifiers: (value: ICartAddModifierChoice[]) => void;
  hasError?: boolean;
};

const ExtrasBlock: React.FC<ExtrasBlockProps> = ({
  modifier,
  cartModifiers,
  setCartModifiers,
  hasError = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const products = useAppSelector(selectProducts);

  const [addedItems, setAddedItems] = useState<number[]>(
    cartModifiers.map((e) => e.ProductModifierChoiceID),
  );
  const [blockAddedItems, setBlockAddedItems] = useState<number[]>([]);
  const [hasMaxSelectionChoiceError, setHasMaxSelectionChoiceError] =
    useState<boolean>(false);

  const [previousSelection, setPreviousSelection] = useState<number>();
  const [hasDefaultSelected, setHasDefaultSelected] = useState(false);

  useEffect(() => {
    setAddedItems(cartModifiers.map((e) => e.ProductModifierChoiceID));
    setBlockAddedItems(
      cartModifiers
        .filter((m) =>
          modifier.Choices.find((c) => m.ProductModifierChoiceID === c.ID),
        )
        .map((e) => e.ProductModifierChoiceID),
    );
  }, [cartModifiers]);

  const isMandatory =
    modifier?.Constraint == ProductModifierConstraints.MANDATORY;

  const isTextual = modifier.DisplayMode === ProductModifierDisplayMode.TEXTUAL;

  return (
    <>
      <Box
        id={`modifier-${modifier.ID}`}
        key={modifier.ID}
        classNames={classes.extrasBox}
      >
        <div className={classes.headerBlock}>
          <div className={classes.header}>
            {modifier.LocalName.concat(isMandatory ? "*" : "")}
          </div>
          {(isMandatory || modifier.SelectionMaxChoiceCount > 0) && (
            <div
              className={classnames([
                classes.mandatoryOption,
                { [classes.error]: hasError || hasMaxSelectionChoiceError },
              ])}
            >
              {modifier.SelectionMaxChoiceCount > 0 && !isMandatory
                ? t("product_extras_selection_max_choice_msg").replace(
                    "%limit%",
                    String(modifier.SelectionMaxChoiceCount || ""),
                  )
                : t("product_modifier_error")}
            </div>
          )}
        </div>
        <div className={isTextual ? undefined : classes.imgBlock}>
          {modifier.Choices.toSorted((a, b) =>
            a.SortOrder < b.SortOrder ? -1 : 1,
          )
            .map((choice) => {
              const product = products?.find((p) => p.id === choice.ProductID);

              return { choice, product };
            })
            .filter(({ choice, product }) => {
              // Products with ID = 0 are add-on products which don't really exist separately (milk for coffee)
              // If choice has a ProductID > 0 and no product is found then do not display product (out of stock)
              if (!product && choice.ProductID != 0) {
                return false;
              }

              return true;
            })
            .map(({ choice, product }) => (
              <ModifierChoice
                key={choice.ID}
                modifier={modifier}
                choice={choice}
                product={product}
                cartModifiers={cartModifiers}
                setCartModifiers={setCartModifiers}
                setHasMaxSelectionChoiceError={setHasMaxSelectionChoiceError}
                blockAddedItems={blockAddedItems}
                addedItems={addedItems}
                isMandatory={isMandatory}
                isTextual={isTextual}
                previousSelection={previousSelection}
                setPreviousSelection={setPreviousSelection}
                hasDefaultSelected={hasDefaultSelected}
                setHasDefaultSelected={setHasDefaultSelected}
              />
            ))}
        </div>
      </Box>
    </>
  );
};

export default ExtrasBlock;
